/*------------------------------*
 | @author  Cameron Slupeiks    |
 | @date    2020.08.26          |
 | Copyright Brash Inc. (2020)  |
 *------------------------------*/

import mapboxgl, { EasingOptions } from "mapbox-gl";
import React, {
	useCallback,
	useContext,
	useEffect,
	useEffectEvent,
	useMemo,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import {
	addImagesToMapboxStyle,
	useListenerSetupMapEffect,
	useMapReadyEmitter,
	useStyleDependantMapEffect,
} from "../../helper/map-helper";
import { mapboxStyle, mapboxStyleSatellite } from "../../constants";
import whitelabel from "../../whitelabel";
import {
	BoundingBox,
	CreateNewSiteFloorParams,
	DeviceCategory,
	isDeviceLocated,
	isDevicePaired,
	mapboxToInternalBounds,
	NotFoundError,
	PairedLocatedVelavuDevice,
	VelavuAPI,
	VelavuAsset,
	VelavuDevice,
	VelavuFloor,
	VelavuGeofence,
	VelavuRoute,
	VelavuRouteDetailed,
	VelavuSite,
} from "velavu-js-api";
import { default as VelavuAssetDevicePair } from "../../data/asset-device-pair";
import {
	resolveSiteBoundaryViewport,
	resolveSiteZoomLevel,
} from "../../data/device-marker";
import MapStyle from "../../data/map-style";
import {
	DEFAULT_IMAGE_POSITIONING,
	imagePositioningToEdgeCoords,
} from "../../state/floor/image-positioning";
import { userImageDataToBase64 } from "../../data/user-image-data";
import UnpairAssetTag from "../../data/unpair-asset-tag";
import { VelavuModalProvider } from "../../elements/velavu-modal";
import { useGeofences, useSites } from "../../helper/api-helper";
import { compareArrays } from "../../helper/array-helper";
import {
	getFloorTarget,
	getLocationFloorID,
	isSiteFloorFilterable,
} from "../../helper/floor-helper";
import {
	useCancellableEffect,
	useElementSize,
	useToggleable,
} from "../../helper/hook-helper";
import useMultiRepo, {
	useSyncMultiRepoSource,
} from "../../helper/multi-repo-hook-helper";
import SimpleObservable from "../../helper/simple-observable";
import { makeStyleable } from "../../helper/icon-helper";
import { classArr } from "../../helper/style-helper";
import useDeviceMarkers, {
	SiteFloorFilter,
} from "../../hook/use-device-markers";
import useMapboxInjectableAnchorsRoutes from "../../injectable/mapbox/mapbox-injectable-anchors-routes";
import useMapboxInjectableDevices from "../../injectable/mapbox/mapbox-injectable-devices";
import useMapboxInjectableDrawWalls, {
	MapboxInjectableDrawWallsParams,
} from "../../injectable/mapbox/mapbox-injectable-draw-walls";
import useMapboxInjectableGeofenceEditor, {
	MapboxInjectableGeofenceEditorToolType,
} from "../../injectable/mapbox/mapbox-injectable-geofence-editor";
import useMapboxInjectableGeofences from "../../injectable/mapbox/mapbox-injectable-geofences";
import useMapboxInjectablePlaceImage, {
	MapboxInjectablePlaceImageParams,
} from "../../injectable/mapbox/mapbox-injectable-place-image";
import useMapboxInjectableRoute, {
	RouteRenderType,
} from "../../injectable/mapbox/mapbox-injectable-route";
import useMapboxInjectableSiteBoundaries from "../../injectable/mapbox/mapbox-injectable-site-boundaries";
import useMapboxInjectableSites from "../../injectable/mapbox/mapbox-injectable-sites";
import useMapboxInjectableSiteIndicators from "../../injectable/mapbox/mapbox-injectable-site-indicators";
import { useSignInContext } from "../sign-in-context/sign-in-context";
import { useMapDevicesState } from "../../state/map-devices-state";
import FloorSelector from "../../widgets/floor-selector";
import MapControls from "../../widgets/map-controls";
import IconArrowRange from "../../dynamicicons/icon-arrow-range";
import IconVelavuGeofence from "../../dynamicicons/icon-velavu-geofence";
import IconVelavuSite from "../../dynamicicons/icon-velavu-site";
import MapContextMenu, {
	MapContextMenuItem,
} from "../map-context-menu/map-context-menu";
import useMapboxInjectableMeasureDistance, {
	isMeasureDistanceLinePlacing,
	MeasureDistanceLine,
} from "../../injectable/mapbox/mapbox-injectable-measure-distance";
import useDashboardMarkersRepo, {
	useSyncDashboardMarkersDevicesSource,
	useSyncDashboardMarkersSingleDevice,
} from "./dashboard-devices-repo";
import * as styles from "./dashboard.module.scss";
import ButtonAssets from "./dropdown/button-assets";
import SiteCreate from "./dropdown/site-create/site-create";
import SiteDetail, { SiteDetailsTab } from "./dropdown/site-detail/site-detail";
import Layers from "./layers/layers";
import PaginationToast from "./toast/pagination-toast";
import polyline from "@mapbox/polyline";
import { MapList } from "./dropdown/map-list/map-list";
import { MapListDisplayType } from "./dropdown/map-list/map-list-display-type";
import GeofenceCreate from "./dropdown/geofence-create/geofence-create";
import GeofenceDetail from "./dropdown/geofence-detail/geofence-detail";
import {
	GeoTransformer,
	mapCanvasToCenterCoords,
} from "../../helper/geo-helper";
import { doBoundsIntersect } from "../../helper/math-helper";
import useMapboxInjectableFloorplanImage from "../../injectable/mapbox/mapbox-injectable-floorplan-image";
import { useSiteDetailState } from "./dropdown/site-detail/site-detail-state";
import booleanContains from "@turf/boolean-contains";
import bboxPolygon from "@turf/bbox-polygon";
import FloorFilter from "../../data/floor-filter";
import { useVelavuToast } from "../../elements/velavu-toast";
import { ToastNotificationNoContent } from "../../elements/toast-notification-builtins";
import { useProvideContentRefreshable } from "../../elements/refresh-context";
import { useDashboardSelectionState } from "./dashboard-selection-state";
import distance from "@turf/distance";
import useFloorCreateState from "../../state/floor/floor-create-state";
import { FloorCreateStep } from "../../state/floor/floor-create-step";
import FloorCreate from "./dropdown/floor-create/floor-create";
import {
	FloorEditStateMode,
	useFloorEditState,
} from "../../state/floor/floor-edit-state";
import FloorDetail from "./dropdown/floor-detail/floor-detail";
import { FloorplanImage } from "../../data/floorplan-image";
import { floorWallsToVelavuFloorGeometry } from "../../state/floor/floor-wall-helper";
import { CascadingPanelStackProvider } from "../../elements/cascading-panel-stack";
import {
	PinGatewayStep,
	usePinGatewayState,
} from "../../state/pin-gateway-state";
import PinGateway from "./dropdown/pin-gateway/pin-gateway";
import useMapboxInjectablePositionGateway from "../../injectable/mapbox/mapbox-injectable-position-gateway";
import { usePinAnchorState } from "../../state/pin-anchor-state";
import PinAnchor from "./dropdown/pin-anchor/pin-anchor";
import useMapboxInjectablePositionAnchor from "../../injectable/mapbox/mapbox-injectable-position-anchor";
import { DeviceType, resolveDeviceType } from "../../helper/device-type-helper";
import DeviceDetail from "./dropdown/device-detail/device-detail";
import { useSiteDevicesCache } from "./dropdown/site-detail/site-devices-cache";

const SITE_VIEWPORT_PADDING = {
	// Account for the width of the dropdown panel
	left: 380,
};

export default function Dashboard(props: {
	resizeObservable?: SimpleObservable<void>;
	onDeepLinkSelectDevice?: VoidFunction;
}) {
	const { t } = useTranslation();

	useEffect(() => {
		//Update the document title
		document.title = `${whitelabel.name} | ${t("dashboard.page_title")}`;
	}, [t]);

	const { pushToast } = useVelavuToast();

	const navigate = useNavigate();
	const { deviceID: deepLinkDeviceID } = useParams<{ deviceID?: string }>();

	//Map object
	const [map, setMap] = useState<mapboxgl.Map | undefined>(undefined);
	const mapContainer = useRef<HTMLDivElement>(null);
	const mapInstance = useMapReadyEmitter(map, async (map) => {
		await addImagesToMapboxStyle(map);
	});

	const mapContainerDimensions = useElementSize(mapContainer);

	//Map style
	const [mapStyle, setMapStyle] = useState(MapStyle.Graphic);
	const toggleMapStyle = useCallback(() => {
		setMapStyle((mapStyle) => {
			if (mapStyle === MapStyle.Graphic) {
				return MapStyle.Satellite;
			} else {
				return MapStyle.Graphic;
			}
		});
	}, [setMapStyle]);

	//Map properties
	const [mapBounds, setMapBounds] = useState<mapboxgl.LngLatBounds | null>(
		null,
	);

	//Devices
	const [pairedDevicesState, pairedDevicesActions] = useMapDevicesState();
	const pairedDevicesActionsLoadRegion = pairedDevicesActions.loadRegion;
	const pairedDevicesActionsLoadMore = pairedDevicesActions.loadMore;

	const mapMovedSinceLastFetch = useMemo((): boolean => {
		if (mapBounds === null) {
			return false;
		}

		if (pairedDevicesState.activeRegion === null) {
			return true;
		}

		return !compareArrays(
			mapboxToInternalBounds(mapBounds),
			pairedDevicesState.activeRegion,
		);
	}, [mapBounds, pairedDevicesState.activeRegion]);
	const mapRegionInsideActiveRegion = useMemo((): boolean => {
		if (mapBounds === null) {
			return false;
		}

		if (pairedDevicesState.activeRegion === null) {
			return false;
		}

		return booleanContains(
			bboxPolygon(pairedDevicesState.activeRegion),
			bboxPolygon(mapboxToInternalBounds(mapBounds)),
		);
	}, [mapBounds, pairedDevicesState.activeRegion]);

	const reloadDevicesInRegion = useCallback(async () => {
		//Refuse to load if we are not in a state to do so
		if (pairedDevicesState.isLoading) {
			return;
		}

		//Refuse to load if we have no map bounds
		if (mapBounds === null) {
			return;
		}

		// If we were previously able to load all content,
		// assume that we will still be able to and load
		// without a bounding box
		let targetBounds: BoundingBox | null;
		if (pairedDevicesState.isComplete) {
			targetBounds = null;
		} else {
			targetBounds = mapboxToInternalBounds(mapBounds);
		}

		//Load the devices in the map region
		await pairedDevicesActionsLoadRegion(targetBounds);
	}, [
		pairedDevicesState.isLoading,
		pairedDevicesState.isComplete,
		pairedDevicesActionsLoadRegion,
		mapBounds,
	]);
	useProvideContentRefreshable(reloadDevicesInRegion);

	const loadMoreDevices = useCallback(() => {
		//Refuse to load if we are not in a state to do so
		if (pairedDevicesState.isLoading) {
			return;
		}

		if (mapMovedSinceLastFetch) {
			//Load devices in the new region
			if (mapBounds !== null) {
				pairedDevicesActionsLoadRegion(
					mapboxToInternalBounds(mapBounds),
				);
			}
		} else {
			//Load more devices in the current region
			if (pairedDevicesState.hasMore) {
				pairedDevicesActionsLoadMore();
			}
		}
	}, [
		pairedDevicesState.hasMore,
		pairedDevicesState.isLoading,
		pairedDevicesActionsLoadMore,
		pairedDevicesActionsLoadRegion,
		mapMovedSinceLastFetch,
		mapBounds,
	]);

	const assets = useMemo(() => {
		return pairedDevicesState.devices.map((device) => device.asset);
	}, [pairedDevicesState.devices]);

	const [dropdownExpanded, setDropdownExpanded, setDropdownCollapsed] =
		useToggleable(false);

	const {
		device: selectedDevice,
		select: setSelectedDevice,
		deviceDetailTab,
		setDeviceDetailTab,
		refresh: refreshDashboardSelection,
	} = useDashboardSelectionState();
	useProvideContentRefreshable(refreshDashboardSelection);

	//Resolve the effective type of the selected device
	const selectedDeviceEffectiveType = useMemo(():
		DeviceType | undefined | null => {
		if (selectedDevice === undefined) {
			return undefined;
		}
		return resolveDeviceType(selectedDevice);
	}, [selectedDevice]);

	const selectDevice = useCallback(
		(device: string | undefined) => {
			if (device !== undefined) {
				navigate(`/map/device/${device}`);
			} else {
				navigate("/map");
			}
		},
		[navigate],
	);

	const [hoveredDeviceID, setHoveredDeviceID] = useState<string | undefined>(
		undefined,
	);

	const [hoveredGeofenceID, setHoveredGeofenceID] = useState<
		string | undefined
	>(undefined);

	const [hoverRoute, setHoverRoute] = useState<VelavuRoute | undefined>(
		undefined,
	);
	const [selectedRoute, setSelectedRoute] = useState<VelavuRoute | undefined>(
		undefined,
	);
	const [selectedRouteDetails, setSelectedRouteDetails] = useState<
		VelavuRouteDetailed | undefined
	>(undefined);

	//Use sites
	const {
		sites,
		setSites,
		lookUpSite: lookUpSiteByID,
		refreshSites,
	} = useSites();

	//The selected site is the site targeted on the map
	const [selectedSiteID, setSelectedSiteID] = useState<string | undefined>(
		undefined,
	);
	const selectedSiteInstance = lookUpSiteByID(selectedSiteID);

	const [sitesFloorOverride, setSitesFloorOverride] = useState<{
		[key: string]: string | undefined;
	}>({});

	//Resolve the floor selections for all sites
	const selectedFilterableFloors = useMemo(() => {
		if (sites === undefined) {
			return {};
		}

		return sites.reduce<{ [siteID: string]: string | undefined }>(
			(acc, site) => {
				//Ignore if this site is not filterable by floor
				if (!isSiteFloorFilterable(site)) {
					return acc;
				}

				//Get the selected floor for this site
				acc[site.id] = getFloorTarget(
					site.floors,
					sitesFloorOverride[site.id],
				)?.id;
				return acc;
			},
			{},
		);
	}, [sites, sitesFloorOverride]);

	//Selects the floor for the site and floor location of the given device
	const setSiteFloorOverrideForDevice = useCallback(
		(device: VelavuDevice | undefined) => {
			if (device === undefined) {
				return;
			}

			const siteID = device.site_id;
			const locationFloorID = getLocationFloorID(device.location);

			if (siteID !== undefined && locationFloorID !== undefined) {
				setSitesFloorOverride((sitesFloorOverride) => ({
					...sitesFloorOverride,
					[siteID]: locationFloorID,
				}));
			}
		},
		[setSitesFloorOverride],
	);

	const [isSiteCreator, openSiteCreator, closeSiteCreator] = useToggleable();
	const [siteCreatorLngLat, setSiteCreatorLngLat] = useState<
		mapboxgl.LngLat | undefined
	>();
	const [isSiteEditor, openSiteEditor, closeSiteEditor] = useToggleable();
	const [clickMenu, setClickMenu] = useState<React.ReactNode>();

	//The inspected site is the site selected by the user
	const [inspectedSiteID, setInspectedSiteID] = useState<string | undefined>(
		undefined,
	);

	useProvideContentRefreshable(
		useCallback(async () => {
			//Request a refresh of the sites
			const newSites = await refreshSites();

			if (inspectedSiteID !== undefined) {
				//Check which sites have been removed
				const oldSiteIDs = sites?.map((site) => site.id) ?? [];
				const newSiteIDs = newSites.map((site) => site.id);
				const removedSiteIDs = oldSiteIDs.filter(
					(siteID) => !newSiteIDs.includes(siteID),
				);

				if (removedSiteIDs.includes(inspectedSiteID)) {
					//Deselect the site
					setInspectedSiteID(undefined);
					pushToast((close) => (
						<ToastNotificationNoContent onDismiss={close} />
					));
				}
			}
		}, [
			refreshSites,
			sites,
			inspectedSiteID,
			setInspectedSiteID,
			pushToast,
		]),
	);

	const inspectedSiteInstance = lookUpSiteByID(inspectedSiteID);
	const inspectedSiteSelectedFloorID = useMemo(() => {
		if (inspectedSiteInstance === undefined) {
			return undefined;
		} else {
			return getFloorTarget(
				inspectedSiteInstance.floors,
				sitesFloorOverride[inspectedSiteInstance.id],
			)?.id;
		}
	}, [inspectedSiteInstance, sitesFloorOverride]);

	const [floorFilter, setFloorFilter] = useState(FloorFilter.Current);
	const doesInspectedSiteSupportFloorFilter: boolean =
		inspectedSiteInstance !== undefined &&
		isSiteFloorFilterable(inspectedSiteInstance);

	const siteDevicesCache = useSiteDevicesCache();
	useProvideContentRefreshable(siteDevicesCache.refresh);

	const siteDetailState = useSiteDetailState(
		siteDevicesCache,
		inspectedSiteInstance?.id,
		doesInspectedSiteSupportFloorFilter ? floorFilter : FloorFilter.All,
		inspectedSiteSelectedFloorID,
	);
	const setSiteSelectedTab = siteDetailState.setSelectedTab;

	const deleteFloorPlan = useCallback(
		(floor: VelavuFloor) => {
			//Ignore if we don't have any sites or there is no inspected site
			if (sites === undefined || inspectedSiteID === undefined) {
				return;
			}

			//Find the inspected site
			const inspectedSiteIndex = sites.findIndex(
				(site) => site.id === inspectedSiteID,
			);
			if (inspectedSiteIndex === -1) {
				return;
			}
			const inspectedSite = sites[inspectedSiteIndex];

			//Remove the floor from the inspected site
			const floorsUpdated = inspectedSite.floors?.filter(
				(predicateFloor) => {
					return predicateFloor.id !== floor.id;
				},
			);

			//Update the inspected site
			const newSites = [...sites];
			newSites[inspectedSiteIndex] = {
				...newSites[inspectedSiteIndex],
				floors: floorsUpdated,
			};
			setSites(newSites);
		},
		[sites, inspectedSiteID, setSites],
	);

	//Geofences
	const [geofences, setGeofences, refreshGeofences] = useGeofences();

	const [selectedGeofenceID, setSelectedGeofenceID] = useState<
		string | undefined
	>(undefined);

	useProvideContentRefreshable(
		useCallback(async () => {
			//Request a refresh of the geofences
			const newGeofences = await refreshGeofences();

			if (selectedGeofenceID !== undefined) {
				//Check which geofences have been removed
				const oldGeofenceIDs =
					geofences?.map((geofence) => geofence.id) ?? [];
				const newGeofenceIDs = newGeofences.map(
					(geofence) => geofence.id,
				);
				const removedGeofenceIDs = oldGeofenceIDs.filter(
					(geofenceIDs) => !newGeofenceIDs.includes(geofenceIDs),
				);

				if (removedGeofenceIDs.includes(selectedGeofenceID)) {
					//Deselect the geofence
					setSelectedGeofenceID(undefined);
					pushToast((close) => (
						<ToastNotificationNoContent onDismiss={close} />
					));
				}
			}
		}, [
			refreshGeofences,
			geofences,
			selectedGeofenceID,
			setSelectedGeofenceID,
			pushToast,
		]),
	);

	const selectedGeofence = useMemo((): VelavuGeofence | undefined => {
		if (selectedGeofenceID === undefined) {
			return undefined;
		} else {
			return geofences?.find(
				(geofence) => geofence.id === selectedGeofenceID,
			);
		}
	}, [geofences, selectedGeofenceID]);
	const selectGeofence = useCallback(
		(geofenceID: string) => {
			//Clear any other selections
			selectDevice(undefined);
			setSelectedRoute(undefined);
			setSelectedRouteDetails(undefined);
			setInspectedSiteID(undefined);

			//Set the selected geofence ID
			setSelectedGeofenceID(geofenceID);

			//Locate the geofence
			const targetGeofence = geofences?.find(
				(geofence) => geofence.id === geofenceID,
			);

			//Fly to the geofence
			if (targetGeofence !== undefined && map !== undefined) {
				const bounds = new mapboxgl.LngLatBounds();
				for (const point of targetGeofence.boundary.coordinates[0] as [
					number,
					number,
				][]) {
					bounds.extend(point);
				}

				map.fitBounds(bounds, { padding: 256 });
			}
		},
		[selectDevice, geofences, map],
	);

	const [geofenceEditorExistingTarget, setGeofenceEditorExistingTarget] =
		useState<string | undefined>(undefined);
	const [isGeofenceEditor, openGeofenceEditor, closeGeofenceEditor] =
		useToggleable();
	const [geofenceEditorToolType, setGeofenceEditorToolType] =
		useState<MapboxInjectableGeofenceEditorToolType>("draw");
	const [geofenceEditorName, setGeofenceEditorName] = useState<string>("");
	const [geofenceEditorPoints, setGeofenceEditorPoints] = useState<
		[number, number][]
	>([]);

	//Cleans up and starts a new geofence editor session
	const startGeofenceEditor = useCallback(() => {
		openGeofenceEditor();
		setGeofenceEditorToolType("draw");
		setGeofenceEditorName("");
		setGeofenceEditorPoints([]);
		setGeofenceEditorExistingTarget(undefined);
	}, [
		openGeofenceEditor,
		setGeofenceEditorToolType,
		setGeofenceEditorPoints,
	]);

	const startGeofenceEditorFromExisting = useCallback(
		(geofence: VelavuGeofence) => {
			openGeofenceEditor();
			setGeofenceEditorToolType("edit");
			setGeofenceEditorName(geofence.name);
			setGeofenceEditorPoints(
				geofence.boundary.coordinates[0] as [number, number][],
			);
			setGeofenceEditorExistingTarget(geofence.id);
		},
		[
			openGeofenceEditor,
			setGeofenceEditorToolType,
			setGeofenceEditorPoints,
		],
	);

	//Submits a new geofence for creation or patching
	const [isGeofenceSubmissionLoading, setGeofenceSubmissionLoading] =
		useState(false);
	const submitGeofence = useCallback(async () => {
		setGeofenceSubmissionLoading(true);

		//Submit the geofence to the backend
		try {
			if (geofenceEditorExistingTarget === undefined) {
				const newGeofence = await VelavuAPI.geofences.createNewGeofence(
					{
						name: geofenceEditorName,
						boundary: {
							type: "Polygon",
							coordinates: [geofenceEditorPoints],
						},
					},
				);

				//Add the geofence to the list
				setGeofences((geofences) => {
					return [...(geofences ?? []), newGeofence];
				});
			} else {
				const updatedGeofence =
					await VelavuAPI.geofences.modifyExistingGeofence(
						geofenceEditorExistingTarget,
						{
							name: geofenceEditorName,
							boundary: {
								type: "Polygon",
								coordinates: [geofenceEditorPoints],
							},
						},
					);

				//Update the geofence
				setGeofences((geofences) => {
					if (geofences === undefined) {
						return undefined;
					}

					const index = geofences.findIndex(
						(geofence) =>
							geofence.id === geofenceEditorExistingTarget,
					);
					if (index !== -1) {
						const newGeofences = [...geofences];
						newGeofences[index] = updatedGeofence;
						return newGeofences;
					} else {
						return geofences;
					}
				});
			}
		} catch (error) {
			console.warn(error);
			return;
		} finally {
			setGeofenceSubmissionLoading(false);
		}
		closeGeofenceEditor();
	}, [
		closeGeofenceEditor,
		geofenceEditorPoints,
		setGeofenceSubmissionLoading,
		setGeofences,
		geofenceEditorExistingTarget,
		geofenceEditorName,
	]);

	//Floor editor
	const [floorCreateState, floorCreateActions] = useFloorCreateState();
	const [floorCreateLoading, setFloorCreateLoading] = useState(false);
	const floorEditState = useFloorEditState(
		useCallback(
			(updatedFloor, updatedSite) => {
				setSites((sites) => {
					if (sites === undefined) {
						return undefined;
					}
					const idx = sites.findIndex(
						(site) => site.id === updatedSite.id,
					);
					if (idx === -1) {
						return sites;
					}

					const newSites = [...sites];
					newSites[idx] = updatedSite;
					return newSites;
				});
			},
			[setSites],
		),
	);

	const launchFloorPlanEditor = useCallback(() => {
		if (inspectedSiteInstance !== undefined) {
			floorCreateActions.createNewFloor(inspectedSiteInstance);
		}
	}, [inspectedSiteInstance, floorCreateActions]);

	//Device location pinning
	const pinGatewayState = usePinGatewayState(
		sites,
		selectedSiteID,
		sitesFloorOverride,
	);
	const pinAnchorState = usePinAnchorState(
		sites,
		siteDevicesCache,
		selectedSiteID,
		sitesFloorOverride,
	);

	//Layers
	const [showLayerEvents, setShowLayerEvents] = useState(true);
	const [showLayerSpeeding, setShowLayerSpeeding] = useState(true);

	//Cognito user
	const userContext = useSignInContext();
	const hasPermission = userContext.hasPermission;

	//Create repos to consolidate devices
	const dashboardSelectableDevicesRepo = useMultiRepo<VelavuDevice>(
		useCallback((device: VelavuDevice) => {
			return device.id;
		}, []),
	);
	const dashboardMarkersRepo = useDashboardMarkersRepo();

	const isAnyEditorOpen: boolean =
		isGeofenceEditor ||
		floorCreateState.activeSite !== null ||
		floorEditState.floor !== null ||
		pinGatewayState.selection !== null ||
		pinAnchorState.selection !== null;

	const [measureLine, setMeasureLine] = useState<MeasureDistanceLine | null>(
		null,
	);
	const isPlacingMeasureLine = isMeasureDistanceLinePlacing(measureLine);

	//Initialize the map
	useEffect(() => {
		if (map || assets === undefined || !userContext.isMapboxRegistered)
			return;

		const deltaDistance = 100;
		const deltaDegrees = 25;

		const mapbox = new mapboxgl.Map({
			container: mapContainer.current!,
			style: mapboxStyle,
			zoom: 2,
			language: "auto",
		});

		const easing = (t: number) => {
			return t * (2 - t);
		};

		mapbox.on("load", () => {
			//Make sure the map fits its frame
			mapbox.resize();

			//Expand the dropdown to show all assets
			setDropdownExpanded();
		});

		mapbox.getCanvas().addEventListener(
			"keydown",
			function (e) {
				e.preventDefault();
				if (e.key === "ArrowUp") {
					mapbox.panBy([0, -deltaDistance], {
						easing: easing,
					});
				} else if (e.key === "ArrowDown") {
					mapbox.panBy([0, deltaDistance], {
						easing: easing,
					});
				} else if (e.key === "ArrowLeft") {
					mapbox.easeTo({
						bearing: mapbox.getBearing() - deltaDegrees,
						easing: easing,
					});
				} else if (e.key === "ArrowRight") {
					mapbox.easeTo({
						bearing: mapbox.getBearing() + deltaDegrees,
						easing: easing,
					});
				}
			},
			true,
		);

		mapbox.on("move", () => {
			setMapBounds(mapbox.getBounds());
		});

		setMap(mapbox);
	}, [
		assets,
		map,
		setMap,
		userContext.isMapboxRegistered,
		clickMenu,
		setClickMenu,
		setDropdownExpanded,
		setMapBounds,
	]);

	//Zoom in to focus on assets on initial load
	const hasDoneInitialZoom = useRef<boolean>(false);
	useStyleDependantMapEffect(
		mapInstance,
		(map) => {
			if (
				!hasDoneInitialZoom.current &&
				pairedDevicesState.activeRegion === null &&
				pairedDevicesState.devices.length !== 0 &&
				!pairedDevicesState.hasMore
			) {
				//Bring all assets into view
				const bounds = new mapboxgl.LngLatBounds();
				for (const device of pairedDevicesState.devices) {
					bounds.extend(device.location.coordinates);
				}
				map.fitBounds(bounds, { maxZoom: 16, padding: 100 });

				hasDoneInitialZoom.current = true;
			}
		},
		[pairedDevicesState],
	);

	useListenerSetupMapEffect(
		mapInstance.map,
		(map, box) => {
			if (isAnyEditorOpen) return;

			box.on("contextmenu", (e) => {
				const items: MapContextMenuItem[] = [];
				if (hasPermission("sites:write")) {
					items.push({
						icon: makeStyleable(IconVelavuSite),
						label: t("shared.map_context_menu.new_site"),
						onClick: () => {
							setDropdownExpanded();
							setSiteCreatorLngLat(e.lngLat);
							openSiteCreator();
							setClickMenu(undefined);
						},
					});
				}
				if (hasPermission("geofences:write")) {
					items.push({
						icon: makeStyleable(IconVelavuGeofence),
						label: t("shared.map_context_menu.new_geofence"),
						onClick: () => {
							setDropdownExpanded();
							setClickMenu(undefined);

							setTimeout(() => {
								startGeofenceEditor();
							});
						},
					});
				}
				items.push({
					icon: makeStyleable(IconArrowRange),
					label: t("shared.map_context_menu.measure_distance"),
					onClick: () => {
						setClickMenu(undefined);

						//Use timeout to avoid the click passing through to the map
						setTimeout(() => {
							setMeasureLine({
								start: [e.lngLat.lng, e.lngLat.lat],
								end: null,
							});
						});
					},
				});
				if (items.length === 0) return;

				setClickMenu(
					<MapContextMenu
						key={`${e.lngLat.lng}${e.lngLat.lat}`}
						map={map}
						lngLat={e.lngLat}
						items={items}
					/>,
				);
			});

			box.on("click", () => {
				setClickMenu(undefined);
			});
		},
		[
			isAnyEditorOpen,
			openSiteCreator,
			setDropdownExpanded,
			startGeofenceEditor,
			hasPermission,
			t,
			setMeasureLine,
		],
	);

	//Subscribe to resize updates
	useEffect(() => {
		if (!props.resizeObservable || !map) return;

		const subscriber = () => {
			map.resize();
			requestAnimationFrame(() => {
				map.resize();
			});
		};
		props.resizeObservable.subscribe(subscriber);
		return () => props.resizeObservable!.unsubscribe(subscriber);
	}, [props.resizeObservable, map]);

	//Fly to the provided coordinates
	const flyTo = useCallback(
		(coordinates: [number, number]) => {
			if (!map) return;

			//Initialize the fly to options
			const options: EasingOptions = {
				center: coordinates,
			};

			const DEFAULT_ZOOM: number = 20;
			const mapZoom = map.getZoom();

			// Set the zoom unless we're selecting an item within the screen bounds,
			// and we're already zoomed in further than the default zoom
			const bounds = map.getBounds();
			if (
				bounds === null ||
				!bounds.contains(coordinates) ||
				mapZoom < DEFAULT_ZOOM
			) {
				options.zoom = DEFAULT_ZOOM;
			}

			const mapCenter = map.getCenter();
			const mapCoordinates: [number, number] = [
				mapCenter.lng,
				mapCenter.lat,
			];
			const flyDistance = distance(coordinates, mapCoordinates, {
				units: "meters",
			});

			// Disable the curve if we're zoomed in more than the default zoom, so
			// that we don't zoom out and trigger any visibility changes during the
			// fly animation
			if (mapZoom <= DEFAULT_ZOOM && flyDistance < 500) {
				map.easeTo(options);
			} else {
				map.flyTo(options);
			}
		},
		[map],
	);

	//Fly to the currently selected device
	const flyToDevice = useCallback(() => {
		if (selectedDevice === undefined) return;
		const coordinates = selectedDevice.location?.coordinates;
		if (coordinates !== undefined) {
			flyTo(coordinates);
		}
	}, [selectedDevice, flyTo]);

	const focusDeviceOnMap = useCallback(() => {
		//Fly to the selected device and enable focus
		flyToDevice();
	}, [flyToDevice]);

	//Deep linking logic
	{
		//Selects a device referenced by its ID (from a deep link)
		const applySelectedDevice = useEffectEvent(
			async (deviceID: string | undefined, abortSignal: AbortSignal) => {
				function applyDeviceSelection(
					device: VelavuDevice | undefined,
				) {
					setSelectedRoute(undefined);
					setSelectedRouteDetails(undefined);
					setHoveredDeviceID(undefined);
					setSiteFloorOverrideForDevice(device);

					if (device !== undefined) {
						hasDoneInitialZoom.current = true;
						setSelectedDevice(device);

						switch (resolveDeviceType(device)) {
							case DeviceType.Anchor:
								setSiteSelectedTab(SiteDetailsTab.Anchors);
								setInspectedSiteID(device.site_id);
								break;
							case DeviceType.Gateway:
								setSiteSelectedTab(SiteDetailsTab.Gateways);
								setInspectedSiteID(device.site_id);
								break;
						}

						if (device.location !== undefined) {
							flyTo(device.location.coordinates);
						}
					} else {
						setSelectedDevice(undefined);
					}
				}

				//If we don't have a device, clear the device selection
				if (deviceID === undefined) {
					applyDeviceSelection(undefined);
					return;
				}

				//Call the props callback
				props.onDeepLinkSelectDevice?.();

				//Try to find the device locally first
				const localDevice = dashboardSelectableDevicesRepo.items.find(
					(device) => device.id === deviceID,
				);
				if (localDevice !== undefined) {
					applyDeviceSelection(localDevice);
					return;
				}

				//Fall back to fetching the deep-linked device from the backend
				let remoteDevice: VelavuDevice;
				try {
					remoteDevice =
						await VelavuAPI.devices.getSpecificDevice(deviceID);
				} catch (error) {
					if (error instanceof NotFoundError) {
						setSelectedDevice(undefined);
						navigate("/map", { replace: true });
						pushToast((close) => (
							<ToastNotificationNoContent onDismiss={close} />
						));
						return;
					}
					throw error;
				}

				//Select the device
				if (!abortSignal.aborted) {
					applyDeviceSelection(remoteDevice);
				}
			},
		);

		//Apply the selected device when the device ID in the URL changes
		useEffect(() => {
			//Wait for the map to be loaded
			if (map === undefined) return;

			const abortController = new AbortController();
			applySelectedDevice(deepLinkDeviceID, abortController.signal);

			return () => {
				abortController.abort();
			};
		}, [deepLinkDeviceID, map]);
	}

	//Fit all loaded assets within the bounds of the map
	const fitToMap = useCallback(() => {
		if (!map) return;

		const bounds = new mapboxgl.LngLatBounds();
		for (const device of pairedDevicesState.devices) {
			bounds.extend(device.location.coordinates);
		}
		map.fitBounds(bounds, { maxZoom: 16, padding: 100 });

		//Deselect the currently selected device
		selectDevice(undefined);
		setSelectedRoute(undefined);
		setSelectedRouteDetails(undefined);
	}, [pairedDevicesState.devices, map, selectDevice]);

	//Zoom the map in by 1 zoom level
	const zoomInMap = useCallback(() => {
		if (!map) return;
		map.zoomTo(map.getZoom() + 1);
	}, [map]);

	//Zoom the map out by 1 zoom level
	const zoomOutMap = useCallback(() => {
		if (!map) return;
		map.zoomTo(map.getZoom() - 1);
	}, [map]);

	const selectSite = useCallback(
		(siteID: string | undefined) => {
			setSelectedSiteID(siteID);

			setSiteSelectedTab(0);
			setInspectedSiteID(siteID);

			if (siteID !== undefined) {
				setDropdownExpanded();

				const site = lookUpSiteByID(siteID);
				if (site !== undefined && map !== undefined) {
					const siteViewport = resolveSiteBoundaryViewport(
						site,
						mapContainerDimensions,
						SITE_VIEWPORT_PADDING,
					);
					if (siteViewport !== undefined) {
						map.flyTo(siteViewport);
					} else {
						flyTo(site.coordinates);
					}
				}
			}
		},
		[
			setSiteSelectedTab,
			setSelectedSiteID,
			setInspectedSiteID,
			lookUpSiteByID,
			setDropdownExpanded,
			map,
			flyTo,
			mapContainerDimensions,
		],
	);

	const updateSiteFloorSelection = useCallback(
		(floorID: string | undefined) => {
			if (inspectedSiteID === undefined && selectedSiteID === undefined)
				return;

			if (inspectedSiteID !== undefined) {
				setSitesFloorOverride((sitesFloorOverride) => ({
					...sitesFloorOverride,
					[inspectedSiteID]: floorID,
				}));
			} else if (selectedSiteID !== undefined) {
				setSitesFloorOverride((sitesFloorOverride) => ({
					...sitesFloorOverride,
					[selectedSiteID]: floorID,
				}));
			}
		},
		[inspectedSiteID, selectedSiteID, setSitesFloorOverride],
	);

	const addSiteFloor = useCallback(
		(floor: VelavuFloor, siteID: string) => {
			setSites((sites) => {
				if (sites === undefined) {
					return undefined;
				}

				//Find the site
				const siteIndex = sites.findIndex((site) => site.id === siteID);
				if (siteIndex === -1) {
					return sites;
				}

				//Get the site floors
				const site = sites[siteIndex];
				const siteFloors = site.floors;
				if (siteFloors === undefined) {
					return sites;
				}

				//Add the floor
				const floorArray = [...siteFloors];
				floorArray.push(floor);
				floorArray.sort((a, b) => a.level - b.level);
				const updatedSite: VelavuSite = {
					...site,
					floors: floorArray,
				};
				const updatedSites = [...sites];
				updatedSites[siteIndex] = updatedSite;
				return updatedSites;
			});
		},
		[setSites],
	);

	//Update the map style
	const initialMapStyleSet = useRef(false);
	const mapInstanceInvalidate = mapInstance.invalidate;
	useEffect(() => {
		//Ignore if we don't have a map
		if (map === undefined) {
			return;
		}

		//Don't set the map style on the first round
		if (!initialMapStyleSet.current) {
			initialMapStyleSet.current = true;
			return;
		}

		//Update the map style
		let mapStyleValue: string;
		switch (mapStyle) {
			case MapStyle.Graphic:
				mapStyleValue = mapboxStyle;
				break;
			case MapStyle.Satellite:
				mapStyleValue = mapboxStyleSatellite;
				break;
		}
		mapInstanceInvalidate();
		map.setStyle(mapStyleValue);
	}, [map, mapStyle, mapInstanceInvalidate]);

	//Consolidate selectable devices
	useSyncMultiRepoSource(
		dashboardSelectableDevicesRepo,
		pairedDevicesState.devices,
	);
	useSyncMultiRepoSource(
		dashboardSelectableDevicesRepo,
		siteDetailState.devices,
	);

	//Consolidate device markers
	useSyncDashboardMarkersDevicesSource(
		dashboardMarkersRepo,
		pairedDevicesState.devices,
		inspectedSiteID === undefined,
	);
	useSyncDashboardMarkersDevicesSource(
		dashboardMarkersRepo,
		siteDetailState.mapDisplayDevices,
		inspectedSiteID !== undefined,
	);
	useSyncDashboardMarkersSingleDevice(dashboardMarkersRepo, selectedDevice);

	const targetSiteFloorFilter = useMemo<SiteFloorFilter | undefined>(() => {
		if (floorFilter === FloorFilter.Current) {
			if (inspectedSiteInstance !== undefined) {
				if (
					isSiteFloorFilterable(inspectedSiteInstance) &&
					inspectedSiteSelectedFloorID !== undefined
				) {
					return {
						siteID: inspectedSiteInstance.id,
						floorID: inspectedSiteSelectedFloorID,
					};
				}
			} else if (selectedSiteInstance !== undefined) {
				if (isSiteFloorFilterable(selectedSiteInstance)) {
					const floorTarget = getFloorTarget(
						selectedSiteInstance.floors,
						sitesFloorOverride[selectedSiteInstance.id],
					);
					if (floorTarget !== undefined) {
						return {
							siteID: selectedSiteInstance.id,
							floorID: floorTarget.id,
						};
					}
				}
			}
		}

		return undefined;
	}, [
		inspectedSiteInstance,
		inspectedSiteSelectedFloorID,
		selectedSiteInstance,
		sitesFloorOverride,
		floorFilter,
	]);
	const visibleDeviceMarkers = useDeviceMarkers(
		dashboardMarkersRepo.items,
		sites,
		mapContainerDimensions,
		SITE_VIEWPORT_PADDING,
		floorFilter === FloorFilter.Current
			? selectedFilterableFloors
			: undefined,
	);

	//Fetch detailed route information for the selected route
	useCancellableEffect(
		(addPromise) => {
			if (selectedRoute !== undefined && selectedDevice !== undefined) {
				addPromise(
					VelavuAPI.assetRoutes.getSpecificRoute(
						selectedDevice.id,
						selectedRoute.id,
					),
				)
					.then(setSelectedRouteDetails)
					.catch((error) => console.log(error.response));
			}
		},
		[selectedRoute, selectedDevice, setSelectedRouteDetails],
	);

	useEffect(() => {
		//Ignore if there is no map or selected route
		if (mapInstance.map === undefined || selectedRoute === undefined)
			return;

		//Decode the selected route to GeoJSON
		const lineString = polyline.toGeoJSON(selectedRoute.data.geometry!);

		//Fit the map to show the path
		const bounds = new mapboxgl.LngLatBounds();
		for (const position of lineString.coordinates) {
			bounds.extend(position as [number, number]);
		}

		//Shift the map to contain the selected route
		mapInstance.map.fitBounds(bounds, {
			padding: {
				top: 100,
				right: 100,
				bottom: 100,
				left: 450,
			},
		});
	}, [mapInstance.map, selectedRoute]);

	const referenceImage = useMemo((): FloorplanImage | null => {
		if (floorCreateState.activeSite) {
			return floorCreateState.referenceImage;
		} else if (floorEditState.floor) {
			return floorEditState.referenceImage;
		} else {
			return null;
		}
	}, [
		floorCreateState.activeSite,
		floorCreateState.referenceImage,
		floorEditState.floor,
		floorEditState.referenceImage,
	]);
	useMapboxInjectableFloorplanImage({
		mapInstance: mapInstance,
		image: referenceImage,
	});

	const drawWallsParams = useMemo((): MapboxInjectableDrawWallsParams => {
		if (
			floorCreateState.activeSite &&
			floorCreateState.step === FloorCreateStep.Draw
		) {
			return {
				mapInstance: mapInstance,
				toolType: floorCreateState.drawToolType,
				walls: floorCreateState.drawWalls,
				onChangeWalls: floorCreateActions.setDrawWalls,
			};
		} else if (
			floorEditState.floor &&
			floorEditState.mode === FloorEditStateMode.Draw
		) {
			return {
				mapInstance: mapInstance,
				toolType: floorEditState.drawToolType,
				walls: floorEditState.drawWalls,
				onChangeWalls: floorEditState.setDrawWalls,
			};
		} else {
			return {
				mapInstance: mapInstance,
				disabled: true,
				toolType: "draw",
				walls: [],
				onChangeWalls: () => {},
			};
		}
	}, [
		floorCreateActions.setDrawWalls,
		floorCreateState.activeSite,
		floorCreateState.drawToolType,
		floorCreateState.drawWalls,
		floorCreateState.step,
		floorEditState.drawToolType,
		floorEditState.drawWalls,
		floorEditState.floor,
		floorEditState.mode,
		floorEditState.setDrawWalls,
		mapInstance,
	]);
	useMapboxInjectableDrawWalls(drawWallsParams);

	const isHoverRoute =
		hoverRoute !== undefined && hoverRoute.id !== selectedRoute?.id;
	useMapboxInjectableRoute({
		mapInstance: mapInstance,
		showLayerSpeeding: showLayerSpeeding,
		showLayerEvents: showLayerEvents,
		route: isHoverRoute ? hoverRoute : selectedRoute,
		routeDetails: isHoverRoute ? undefined : selectedRouteDetails,
		renderType: isHoverRoute
			? RouteRenderType.Hover
			: RouteRenderType.Normal,
		disableInteraction: isPlacingMeasureLine,
	});

	const displaySites = useMemo(() => {
		if (sites === undefined) {
			return undefined;
		}

		//Don't show any sites being actively edited
		const hiddenSiteIDs = new Set<string>();
		if (floorCreateState.activeSite) {
			hiddenSiteIDs.add(floorCreateState.activeSite.id);
		}
		if (floorEditState.site) {
			hiddenSiteIDs.add(floorEditState.site.id);
		}

		return sites.filter((site) => !hiddenSiteIDs.has(site.id));
	}, [sites, floorCreateState.activeSite, floorEditState.site]);

	const mapboxLayerIDSiteIndicators = useMapboxInjectableSiteIndicators(
		mapInstance,
		{
			sites: displaySites,
			onSelectSite: selectSite,
			mapDimensions: mapContainerDimensions,
			viewportPadding: SITE_VIEWPORT_PADDING,
			disableInteraction: isPlacingMeasureLine,
		},
	);

	//Hide device markers if repositioning
	const hideDeviceMarkers: boolean =
		pinAnchorState.selection !== null || pinGatewayState.selection !== null;

	const effectiveMapDevicesSource = useMemo(() => {
		if (hideDeviceMarkers) {
			return [];
		} else {
			return visibleDeviceMarkers;
		}
	}, [visibleDeviceMarkers, hideDeviceMarkers]);
	const mapboxLayerIDDevices = useMapboxInjectableDevices({
		mapInstance: mapInstance,
		devicesSource: effectiveMapDevicesSource,
		hoveredDeviceID: hoveredDeviceID,
		selectedDeviceID: selectedDevice?.id,
		onSelectDevice: selectDevice,
		animateMovement: true,
		belowLayer: mapboxLayerIDSiteIndicators,
		disableInteraction: isPlacingMeasureLine,
	});

	const mapboxLayerIDGeofenceEditor = useMapboxInjectableGeofenceEditor({
		mapInstance: mapInstance,
		disabled: !isGeofenceEditor,
		toolType: geofenceEditorToolType,
		onChangeToolType: setGeofenceEditorToolType,
		points: geofenceEditorPoints,
		onChangePoints: setGeofenceEditorPoints,
		belowLayer: mapboxLayerIDDevices,
	});

	const previewGeofences = useMemo<VelavuGeofence[]>(() => {
		if (geofences === undefined) {
			return [];
		}

		if (!isGeofenceEditor || geofenceEditorExistingTarget === undefined) {
			return geofences;
		}

		return geofences.filter(
			(geofence) => geofence.id !== geofenceEditorExistingTarget,
		);
	}, [geofences, isGeofenceEditor, geofenceEditorExistingTarget]);
	const mapboxLayerIDGeofences = useMapboxInjectableGeofences({
		mapInstance: mapInstance,
		geofences: previewGeofences,
		selectable: !isAnyEditorOpen && !isPlacingMeasureLine,
		selectedGeofenceID: selectedGeofenceID,
		hoveredGeofenceID: hoveredGeofenceID,
		onHoverGeofence: setHoveredGeofenceID,
		onSelectGeofence: selectGeofence,
		belowLayer: mapboxLayerIDGeofenceEditor,
	});

	const mapboxLayerIDSiteBoundaries = useMapboxInjectableSiteBoundaries(
		mapInstance,
		{
			sites: displaySites,
			belowLayer: mapboxLayerIDGeofences,
		},
	);

	const highlightedFloorplanSiteIDs = useMemo(() => {
		return [inspectedSiteID, selectedSiteID].filter(
			(id) => id !== undefined,
		);
	}, [inspectedSiteID, selectedSiteID]);
	useMapboxInjectableSites(mapInstance, {
		sites: displaySites,
		floorOverride: sitesFloorOverride,
		highlightedSiteIDs: highlightedFloorplanSiteIDs,
		belowLayer: mapboxLayerIDSiteBoundaries,
	});

	const routeApplicableDevices = useMemo(() => {
		//Show routes for anchor devices while on the anchors tab
		if (siteDetailState.selectedTab === SiteDetailsTab.Anchors) {
			return siteDetailState.allFloorMapDisplayDevices;
		} else {
			return [];
		}
	}, [siteDetailState]);
	const routeHiddenDeviceIDs = useMemo<Set<string>>(() => {
		if (
			floorFilter === FloorFilter.All ||
			inspectedSiteInstance === undefined ||
			!isSiteFloorFilterable(inspectedSiteInstance) ||
			inspectedSiteSelectedFloorID === undefined
		) {
			return new Set();
		} else {
			const otherFloorDevices = routeApplicableDevices.filter(
				(device) => {
					return (
						getLocationFloorID(device.location) !==
						inspectedSiteSelectedFloorID
					);
				},
			);

			return new Set(otherFloorDevices.map((device) => device.id));
		}
	}, [
		floorFilter,
		routeApplicableDevices,
		inspectedSiteInstance,
		inspectedSiteSelectedFloorID,
	]);
	const anchorRoutesZoomThreshold = useMemo(() => {
		return resolveSiteZoomLevel(
			inspectedSiteInstance,
			mapContainerDimensions,
			SITE_VIEWPORT_PADDING,
		);
	}, [inspectedSiteInstance, mapContainerDimensions]);

	const effectiveMapAnchorRoutes = useMemo(() => {
		if (hideDeviceMarkers) {
			return [];
		} else {
			return routeApplicableDevices;
		}
	}, [hideDeviceMarkers, routeApplicableDevices]);
	useMapboxInjectableAnchorsRoutes({
		mapInstance: mapInstance,
		anchors: effectiveMapAnchorRoutes,
		zoomThreshold: anchorRoutesZoomThreshold,
		hiddenAnchorIDs: routeHiddenDeviceIDs,
		beforeLayer: mapboxLayerIDDevices,
		disableInteraction: isPlacingMeasureLine,
	});

	useMapboxInjectablePositionGateway({
		mapInstance: mapInstance,
		disabled:
			!pinGatewayState.selection ||
			pinGatewayState.step !== PinGatewayStep.Position,
		position: pinGatewayState.location,
		setPosition: pinGatewayState.setLocation,
	});
	useMapboxInjectablePositionAnchor({
		mapInstance: mapInstance,
		disabled: !pinAnchorState.selection,
		otherAnchors: pinAnchorState.otherAnchors,
		position: pinAnchorState.location,
		setPosition: pinAnchorState.setLocation,
		positioningFeedback: pinAnchorState.positioningFeedback,
	});

	//Show floor plan if editing
	const placeImageParams = useMemo((): MapboxInjectablePlaceImageParams => {
		if (floorCreateState.activeSite) {
			if (floorCreateState.step === FloorCreateStep.Place) {
				return {
					mapInstance: mapInstance,
					state: floorCreateState.placeImageState,
					onChangeState: floorCreateActions.setPlaceImageState,
					image: floorCreateState.floorImage,
					opacity: floorCreateState.floorImageOpacity,
				};
			}
		} else if (floorEditState.floor) {
			if (floorEditState.isPositioning) {
				return {
					mapInstance: mapInstance,
					state: floorEditState.positioning,
					onChangeState: floorEditState.setPositioning,
					image: floorEditState.image,
					opacity: floorEditState.opacity,
				};
			}
		}

		return {
			mapInstance: mapInstance,
			state: DEFAULT_IMAGE_POSITIONING,
			onChangeState: () => {},
			image: null,
		};
	}, [
		floorCreateActions.setPlaceImageState,
		floorCreateState.activeSite,
		floorCreateState.floorImage,
		floorCreateState.floorImageOpacity,
		floorCreateState.placeImageState,
		floorCreateState.step,
		floorEditState.floor,
		floorEditState.image,
		floorEditState.isPositioning,
		floorEditState.opacity,
		floorEditState.positioning,
		floorEditState.setPositioning,
		mapInstance,
	]);
	useMapboxInjectablePlaceImage(placeImageParams);

	useMapboxInjectableMeasureDistance({
		mapInstance: mapInstance,
		line: measureLine,
		onChangeLine: setMeasureLine,
		measurementSystem: userContext.preferences.measurementSystem,
	});

	let dropdownComponent: React.ReactNode;

	const [mapListDisplayType, setMapListDisplayType] = useState(
		MapListDisplayType.Asset,
	);

	const assetListDevices = useMemo<PairedLocatedVelavuDevice[]>(() => {
		return (
			pairedDevicesState.devices
				//Filter out any devices that aren't within the map bounds
				.filter((device) => {
					if (mapBounds === null) {
						return true;
					}
					return mapBounds.contains(device.location.coordinates);
				})
				//Filter out any devices on non-selected floors
				.filter((device) => {
					return (
						targetSiteFloorFilter === undefined ||
						device.site_id !== targetSiteFloorFilter.siteID ||
						getLocationFloorID(device.location) === undefined ||
						getLocationFloorID(device.location) ===
							targetSiteFloorFilter.floorID
					);
				})
		);
	}, [pairedDevicesState.devices, mapBounds, targetSiteFloorFilter]);

	if (pinGatewayState.selection !== null) {
		dropdownComponent = <PinGateway state={pinGatewayState} />;
	} else if (pinAnchorState.selection !== null) {
		dropdownComponent = <PinAnchor state={pinAnchorState} />;
	} else if (selectedDevice !== undefined) {
		dropdownComponent = (
			<DeviceDetail
				device={selectedDevice}
				onBack={() => {
					selectDevice(undefined);
					setSelectedRoute(undefined);
					setSelectedRouteDetails(undefined);
					setDropdownExpanded();
				}}
				onClose={() => {
					selectDevice(undefined);
					setSelectedRoute(undefined);
					setSelectedRouteDetails(undefined);
					setDropdownCollapsed();
				}}
				focusDeviceOnMap={focusDeviceOnMap}
				onReposition={() => {
					if (selectedDevice.category === DeviceCategory.Anchor) {
						pinAnchorState.setSelection(selectedDevice);
					} else {
						pinGatewayState.setSelection(selectedDevice);
					}
				}}
				onUnpin={() => {
					if (selectedDevice.category === DeviceCategory.Anchor) {
						pinAnchorState.unpin(selectedDevice);
					} else {
						pinGatewayState.unpin(selectedDevice);
					}
				}}
				hoverRoute={hoverRoute}
				setHoverRoute={setHoverRoute}
				selectedRoute={selectedRoute}
				setSelectedRoute={setSelectedRoute}
				onSelectRoute={() => setSelectedRouteDetails(undefined)}
				onDeleteRoute={() => {
					setSelectedRoute(undefined);
					setSelectedRouteDetails(undefined);
				}}
				tab={deviceDetailTab}
				setTab={setDeviceDetailTab}
			/>
		);
	} else if (isGeofenceEditor) {
		dropdownComponent = (
			<GeofenceCreate
				name={geofenceEditorName}
				onChangeName={setGeofenceEditorName}
				onClose={closeGeofenceEditor}
				onSubmit={submitGeofence}
				disabled={
					geofenceEditorToolType === "draw" ||
					isGeofenceSubmissionLoading
				}
				isLoading={isGeofenceSubmissionLoading}
				isEditing={geofenceEditorExistingTarget !== undefined}
			/>
		);
	} else if (floorCreateState.activeSite) {
		dropdownComponent = (
			<FloorCreate
				onClose={floorCreateActions.close}
				onFinish={async () => {
					const floorImage = floorCreateState.floorImage;
					let walls = floorCreateState.drawWalls;

					//User selects Place 2d image on map
					if (walls.length === 0 && floorImage !== null) {
						const transformer = new GeoTransformer(
							floorCreateState.placeImageState.scale,
							floorCreateState.placeImageState.rotation,
							floorCreateState.placeImageState.center,
						);

						//Create walls from image edges
						walls = mapCanvasToCenterCoords([
							[
								{ x: 0, y: 0 },
								{ x: 0, y: floorImage.dimensions.height },
								{
									x: floorImage.dimensions.width,
									y: floorImage.dimensions.height,
								},
								{ x: floorImage.dimensions.width, y: 0 },
								{ x: 0, y: 0 },
							],
						]).map((line) =>
							line.map((point) => transformer.transform(point)),
						);
					}

					//Create the request
					const request: CreateNewSiteFloorParams = {
						name: floorCreateState.metadata.name,
						level: floorCreateState.metadata.level,
						geometry: floorWallsToVelavuFloorGeometry(walls),
					};
					if (floorImage !== null) {
						request.img_upload = await userImageDataToBase64(
							floorImage.renderable,
						);
						request.img = {
							filename: floorImage.name,
							width: floorImage.dimensions.width,
							height: floorImage.dimensions.height,
							rotation: floorCreateState.placeImageState.rotation,
							scale: floorCreateState.placeImageState.scale,
							opacity: floorCreateState.floorImageOpacity,
							center: floorCreateState.placeImageState.center,
							coordinates: imagePositioningToEdgeCoords(
								floorCreateState.placeImageState,
								floorImage.dimensions.width,
								floorImage.dimensions.height,
							),
						};
					}

					//Enable loading
					setFloorCreateLoading(true);

					//Submit to backend
					try {
						const siteID = inspectedSiteID!;

						//Create new floor
						const newFloor =
							await VelavuAPI.siteFloors.createNewSiteFloor(
								siteID,
								request,
							);

						//Refresh the site data
						const newSite =
							await VelavuAPI.sites.getSpecificSite(siteID);

						setSites((sites) => {
							if (!sites) return undefined;

							//Get the site index
							const index = sites.findIndex(
								(site) => site.id === siteID,
							);
							if (index === -1) return sites;

							//Update the site
							const newSites = [...sites];
							newSites[index] = newSite;
							return newSites;
						});

						//Select the floor
						updateSiteFloorSelection(newFloor.id);

						//Close the menu
						floorCreateActions.close();
					} catch (error) {
						console.warn("Failed to update floor", error);
					} finally {
						setFloorCreateLoading(false);
					}
				}}
				disableNext={floorCreateLoading}
				state={floorCreateState}
				actions={floorCreateActions}
			/>
		);
	} else if (!dropdownExpanded) {
		dropdownComponent = <ButtonAssets onClick={setDropdownExpanded} />;
	} else if (selectedGeofence !== undefined) {
		const localSelectedGeofence = selectedGeofence;

		dropdownComponent = (
			<GeofenceDetail
				geofence={localSelectedGeofence}
				onBack={() => setSelectedGeofenceID(undefined)}
				onClose={setDropdownCollapsed}
				onEdit={() => {
					startGeofenceEditorFromExisting(localSelectedGeofence);
				}}
				onDelete={() => {
					setSelectedGeofenceID(undefined);
					setGeofences((geofences) => {
						return geofences?.filter(
							(geofence) =>
								geofence.id !== localSelectedGeofence.id,
						);
					});
				}}
				onSelectDevice={selectDevice}
				onHoverDevice={setHoveredDeviceID}
			/>
		);
	} else if (isSiteCreator) {
		dropdownComponent = (
			<SiteCreate
				key={
					siteCreatorLngLat
						? `${siteCreatorLngLat.lng},${siteCreatorLngLat.lat}`
						: ""
				} // trigger rerender
				from={`${map?.getCenter().lng},${map?.getCenter().lat}`}
				initialCoords={
					siteCreatorLngLat
						? [siteCreatorLngLat.lng, siteCreatorLngLat.lat]
						: undefined
				}
				onClose={() => {
					closeSiteCreator();
					setSiteCreatorLngLat(undefined);
				}}
				onPreview={flyTo}
				onResult={(site) => {
					if (!sites) return;

					//Close panel
					closeSiteCreator();

					//Add the new site to the list
					const newSites = sites.concat(site);
					setSites(newSites);

					//Automatically inspect the new site
					selectSite(site.id);
				}}
			/>
		);
	} else if (inspectedSiteInstance !== undefined) {
		if (isSiteEditor) {
			dropdownComponent = (
				<SiteCreate
					from={`${map?.getCenter().lng},${map?.getCenter().lat}`}
					initialEdit={inspectedSiteInstance}
					onClose={closeSiteEditor}
					onPreview={flyTo}
					onResult={(site) => {
						if (sites === undefined) {
							return;
						}

						//Close panel
						closeSiteEditor();

						//Update the site listing
						const newSites = [...sites];
						const siteIndex = newSites.findIndex(
							(site) => site.id === inspectedSiteInstance.id,
						);
						if (siteIndex !== -1) {
							newSites[siteIndex] = site;
						}
						setSites(newSites);
					}}
				/>
			);
		} else if (floorEditState.floor !== null) {
			dropdownComponent = <FloorDetail floorEditState={floorEditState} />;
		} else {
			dropdownComponent = (
				<SiteDetail
					siteDetailState={siteDetailState}
					onBack={() => {
						setSiteSelectedTab(0);
						setInspectedSiteID(undefined);
					}}
					onClose={() => {
						setSiteSelectedTab(0);
						setDropdownCollapsed();
					}}
					onDeletePlan={deleteFloorPlan}
					onEditFloor={(floor) => {
						floorEditState.open(inspectedSiteInstance!, floor);
					}}
					onCreateFloor={launchFloorPlanEditor}
					onAddFloor={addSiteFloor}
					selectedFloor={inspectedSiteSelectedFloorID}
					onSelectFloor={updateSiteFloorSelection}
					showFloorFilter={doesInspectedSiteSupportFloorFilter}
					floorFilter={floorFilter}
					onSetFloorFilter={setFloorFilter}
					onSelectDevice={selectDevice}
					onHoverDevice={setHoveredDeviceID}
					onEdit={openSiteEditor}
					onDelete={() => {
						if (
							sites === undefined ||
							inspectedSiteID === undefined
						)
							return;

						//Adjusting the inspected site index
						if (selectedSiteID === inspectedSiteID) {
							setSelectedSiteID(undefined);
						}
						setInspectedSiteID(undefined);

						//Removing the site
						const newSites = [...sites];
						const siteIndex = newSites.findIndex(
							(site) => site.id === inspectedSiteID,
						);
						if (siteIndex !== -1) {
							newSites.splice(siteIndex, 1);
						}
						setSites(newSites);
					}}
					site={inspectedSiteInstance!}
					onPinNewGateway={pinGatewayState.setSelection}
					onPinNewAnchor={pinAnchorState.setSelection}
				/>
			);
		}
	} else {
		let showFloorFilter = false;
		let selectedFloorNum: number | undefined = undefined;
		const site = selectedSiteInstance;
		if (site !== undefined) {
			if (isSiteFloorFilterable(site)) {
				showFloorFilter = true;

				if (floorFilter === FloorFilter.Current) {
					selectedFloorNum = getFloorTarget(
						site.floors,
						sitesFloorOverride[site.id],
					)!.level;
				}
			}
		}

		dropdownComponent = (
			<MapList
				displayType={mapListDisplayType}
				onChangeDisplayType={setMapListDisplayType}
				selectedFloorNum={selectedFloorNum}
				onChangeFloorFilter={setFloorFilter}
				showFloorFilter={showFloorFilter}
				onClose={setDropdownCollapsed}
				devices={assetListDevices}
				onSelectDevice={selectDevice}
				onHoverDevice={setHoveredDeviceID}
				sites={sites ?? []}
				onSelectSite={selectSite}
				onCreateSite={openSiteCreator}
				geofences={geofences ?? []}
				onSelectGeofence={selectGeofence}
				onHoverGeofence={setHoveredGeofenceID}
				onCreateGeofence={startGeofenceEditor}
			/>
		);
	}

	const floorSelector = useMemo(() => {
		if (selectedSiteInstance !== undefined) {
			const site = selectedSiteInstance;

			return (
				<FloorSelector
					className={styles.floorSelector}
					site={site}
					onFloorClick={() => {
						setDropdownExpanded();
						setInspectedSiteID(site.id);
					}}
					onSelectFloor={updateSiteFloorSelection}
					selectedFloor={getFloorTarget(
						site.floors,
						sitesFloorOverride[site.id],
					)}
				/>
			);
		}

		return null;
	}, [
		selectedSiteInstance,
		sitesFloorOverride,
		setDropdownExpanded,
		updateSiteFloorSelection,
	]);

	// Hide floor selection widget if site not in view
	useStyleDependantMapEffect(
		mapInstance,
		(map, box) => {
			if (sites === undefined) return;

			box.on("move", () => {
				const mapZoom = map.getZoom();

				const mapBounds = map.getBounds();
				if (mapBounds === null) {
					setSelectedSiteID(undefined);
					return;
				}

				const mapBoundsArray: [number, number, number, number] = [
					mapBounds.getWest(),
					mapBounds.getSouth(),
					mapBounds.getEast(),
					mapBounds.getNorth(),
				];

				const targetSite = sites.find((site) => {
					//Resolve the desired zoom level for the site
					const siteViewport = resolveSiteBoundaryViewport(
						site,
						mapContainerDimensions,
						SITE_VIEWPORT_PADDING,
					);
					if (siteViewport === undefined) {
						//This can happen if the site has no floors.
						//In this case, we should never select the site.
						return false;
					}

					//Check if we are at an appropriate zoom level for this site
					if (mapZoom < siteViewport.zoom) {
						return false;
					}

					//Check if the viewport is visible on the map
					if (
						!doBoundsIntersect(mapBoundsArray, siteViewport.bounds)
					) {
						return false;
					}

					//The site is visible and the map is zoomed in enough,
					//select it!
					return true;
				});

				//No site selection
				if (targetSite !== undefined) {
					setSelectedSiteID(targetSite.id);
				} else {
					setSelectedSiteID(undefined);
				}
			});
		},
		[sites, mapContainerDimensions],
	);

	return (
		<div className={styles.container}>
			<div className={styles.map} ref={mapContainer} />

			<div
				id="border"
				className={classArr(
					styles.mapEditorHighlight,
					isAnyEditorOpen && styles.mapEditorHighlightActive,
				)}
			/>

			<PaginationToast
				show={
					!pairedDevicesState.isComplete &&
					inspectedSiteID === undefined &&
					(pairedDevicesState.hasMore ||
						pairedDevicesState.isLoading ||
						!mapRegionInsideActiveRegion)
				}
				isLoading={pairedDevicesState.isLoading}
				type={mapMovedSinceLastFetch ? "loadArea" : "loadMore"}
				onClickLoad={loadMoreDevices}
			/>

			{floorSelector}
			{clickMenu}

			<MapControls
				className={styles.controls}
				labelFit={t("dashboard.map_controls.show_all_assets")}
				onFit={fitToMap}
				labelMapStyle={
					mapStyle === MapStyle.Graphic
						? t("dashboard.map_controls.satellite")
						: t("dashboard.map_controls.graphic")
				}
				onChangeMapStyle={toggleMapStyle}
				onZoomIn={zoomInMap}
				onZoomOut={zoomOutMap}
			/>

			<VelavuModalProvider zIndex={60}>
				<div className={styles.dropdownWrapper}>
					<CascadingPanelStackProvider
						className={styles.dropdownTransparentWrapper}
					>
						{dropdownComponent}
					</CascadingPanelStackProvider>

					{selectedRoute && (
						<div className={styles.layers}>
							<Layers
								showEvents={showLayerEvents}
								setShowEvents={setShowLayerEvents}
								showSpeeding={showLayerSpeeding}
								setShowSpeeding={setShowLayerSpeeding}
							/>
						</div>
					)}
				</div>
			</VelavuModalProvider>
		</div>
	);
}
